import type { PopupViewKey } from './popup-view';

/**
 * What a pop-up CTA does when tapped. The router (`useCtaRouter`) turns each of
 * these into concrete side effects:
 *  - `dismiss`        — close the pop-up, stay put.
 *  - `navigateLobby`  — close the surface + go to the lobby (`/`).
 *  - `navigateMarket` — close the surface + open the market/event page.
 *  - `openIntercom`   — close the surface + open the support messenger.
 */
export type PopupAction = 'dismiss' | 'navigateLobby' | 'navigateMarket' | 'openIntercom';

/** A single CTA: its Figma label + the action it fires. */
export interface CtaDescriptor {
  label: string;
  action: PopupAction;
}

/** A pop-up's CTA(s): one primary button, plus an optional secondary text link. */
export interface PopupCtaConfig {
  primary: CtaDescriptor;
  /** Rendered as an underlined text link under the primary (e.g. "Contact Support"). */
  secondary?: CtaDescriptor;
}

/**
 * CTA label + action for every PRD pop-up view. **Labels are from Figma**
 * (pixel-fidelity directive); **actions are from the PRD**. Shared config so the
 * built views, the stub views, and any follow-up agent all read from one place.
 *
 * Discrepancy flagged: `order-filled-partial` uses the Figma label "View Order"
 * (the PRD copy said "View position"); Figma wins per the pixel-fidelity
 * directive. Both still route to `navigateMarket`.
 */
export const POPUP_CTA: Record<Exclude<PopupViewKey, 'generic'>, PopupCtaConfig> = {
  'withdrawal-approved': { primary: { label: 'Awesome', action: 'dismiss' } },
  'withdrawal-failed': {
    primary: { label: 'Back to lobby', action: 'navigateLobby' },
    secondary: { label: 'Contact Support', action: 'openIntercom' },
  },
  // Resolution keeps its Figma primary (a plain dismiss — "Awesome" / "Find new
  // markets"), plus a secondary "View market" text link that deep-links to the
  // resolved event page. The link only renders when the notification carries a
  // deep-link id (see the resolution views) — eventless markets have no page.
  'resolution-won-all': {
    primary: { label: 'Awesome', action: 'dismiss' },
    secondary: { label: 'View market', action: 'navigateMarket' },
  },
  'resolution-won-some': {
    primary: { label: 'Awesome', action: 'dismiss' },
    secondary: { label: 'View market', action: 'navigateMarket' },
  },
  'resolution-none': {
    primary: { label: 'Find new markets', action: 'dismiss' },
    secondary: { label: 'View market', action: 'navigateMarket' },
  },
  'position-value-change': { primary: { label: 'View position', action: 'navigateMarket' } },
  'order-filled': { primary: { label: 'View Position', action: 'navigateMarket' } },
  'order-filled-partial': { primary: { label: 'View Order', action: 'navigateMarket' } },
  'new-market': { primary: { label: 'Explore Market', action: 'navigateMarket' } },
  'limit-order-expired': { primary: { label: 'Got it', action: 'dismiss' } },
  'market-closing-soon': { primary: { label: 'Trade Now', action: 'navigateMarket' } },
};
